import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Clock, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PRIORITIES = [
  { value: "low", label: "Bassa", color: "bg-slate-100 text-slate-600" },
  { value: "normal", label: "Normale", color: "bg-blue-50 text-blue-700" },
  { value: "high", label: "Alta", color: "bg-amber-50 text-amber-700" },
  { value: "urgent", label: "Urgente", color: "bg-rose-50 text-rose-700" },
];
const STATUSES = [
  { value: "open", label: "Da fare" },
  { value: "in_progress", label: "In corso" },
  { value: "done", label: "Completato" },
];

export function TasksTab({ order }) {
  const { user } = useAuth();
  const isStaff = ["admin", "support"].includes(user?.role);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assigned_user_id: "", priority: "normal", deadline: "" });

  const load = async () => {
    try { const { data } = await api.get("/tasks", { params: { order_id: order.id } }); setTasks(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    load();
    if (isStaff) api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [order.id]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Inserisci un titolo"); return; }
    try {
      const payload = { ...form, order_id: order.id };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/tasks", payload); toast.success("Task creato");
      setForm({ title: "", description: "", assigned_user_id: "", priority: "normal", deadline: "" });
      setAdding(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/tasks/${id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/tasks/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const userName = (id) => users.find((u) => u.id === id)?.name || (id ? "Assegnato" : "—");
  const isOverdue = (tsk) => tsk.deadline && new Date(tsk.deadline) < new Date() && tsk.status !== "done";

  return (
    <div className="max-w-3xl space-y-4" data-testid="tasks-tab">
      {isStaff && (
        <div>
          {!adding ? (
            <Button onClick={() => setAdding(true)} className="bg-blue-900 hover:bg-blue-800" data-testid="task-add-btn"><Plus size={16} className="mr-1.5" /> Nuovo task</Button>
          ) : (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3" data-testid="task-form">
              <div className="space-y-1.5"><Label>Titolo</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" /></div>
              <div className="space-y-1.5"><Label>Descrizione</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc" /></div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Assegna a</Label>
                  <Select value={form.assigned_user_id} onValueChange={(v) => setForm({ ...form, assigned_user_id: v })}>
                    <SelectTrigger data-testid="task-assignee"><SelectValue placeholder="Utente" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Priorità</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Scadenza</Label><Input type="datetime-local" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="task-deadline" /></div>
              </div>
              <div className="flex gap-2">
                <Button onClick={create} className="bg-blue-900 hover:bg-blue-800" data-testid="task-save-btn">Salva</Button>
                <Button variant="ghost" onClick={() => setAdding(false)}>Annulla</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        {tasks.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Nessun task per questo ordine.</p>}
        {tasks.map((tsk) => {
          const pr = PRIORITIES.find((p) => p.value === tsk.priority) || PRIORITIES[1];
          return (
            <div key={tsk.id} className="bg-white rounded-lg border border-slate-200 p-4" data-testid="task-row">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-800">{tsk.title}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${pr.color}`}>{pr.label}</span>
                    {isOverdue(tsk) && <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 flex items-center gap-1" data-testid="task-overdue"><AlertTriangle size={11} /> In ritardo</span>}
                  </div>
                  {tsk.description && <p className="text-sm text-slate-500 mt-1">{tsk.description}</p>}
                  <div className="text-xs text-slate-400 mt-1 flex items-center gap-3 flex-wrap">
                    <span>{userName(tsk.assigned_user_id)}</span>
                    {tsk.deadline && <span className="flex items-center gap-1"><Clock size={11} /> {formatDateTime(tsk.deadline)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Select value={tsk.status} onValueChange={(v) => setStatus(tsk.id, v)}>
                    <SelectTrigger className="w-32 h-8" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  {isStaff && <button onClick={() => del(tsk.id)} className="text-slate-400 hover:text-rose-600 p-1" data-testid="task-delete-btn"><Trash2 size={15} /></button>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
